import { SftpPushPendingRecordsTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { SftpPushPendingRecordsTableDataModel } from "../hooks/useSftpPushPending";

/**
 * Toggles the selection state for all items on the current page.
 * If all items on the page are selected, it deselects them all.
 * If not all items are selected, it selects them all.
 *
 * @param startIndex - The start index of the current page
 * @param endIndex - The end index of the current page
 * @param setTableData - The state setter for table data
 */
export const toggleSelectAllOnPage = (
  startIndex: number,
  endIndex: number,
  setTableData: React.Dispatch<
    React.SetStateAction<SftpPushPendingRecordsTableDataModel[]>
  >,
) => {
  setTableData((prevData) => {
    // Get the items on the current page
    const pageItems = prevData.slice(startIndex, endIndex + 1);

    // Check if all items on the page are currently selected
    const allSelected = pageItems.every(
      (row) =>
        row[SftpPushPendingRecordsTableColumnKeys.SELECT]?.isChecked === true,
    );

    // Toggle: if all selected, deselect all; otherwise, select all
    const newCheckedState = !allSelected;

    return prevData.map((row, index) => {
      // Only modify items on the current page
      if (index >= startIndex && index <= endIndex) {
        return {
          ...row,
          [SftpPushPendingRecordsTableColumnKeys.SELECT]: {
            ...row[SftpPushPendingRecordsTableColumnKeys.SELECT],
            isChecked: newCheckedState,
          },
        };
      }
      return row;
    });
  });
};

/**
 * Checks if all items on the current page are selected
 *
 * @param tableData - The full table data array
 * @param startIndex - The start index of the current page
 * @param endIndex - The end index of the current page
 * @returns true if all items on the current page are selected
 */
export const areAllPageItemsSelected = (
  tableData: SftpPushPendingRecordsTableDataModel[],
  startIndex: number,
  endIndex: number,
): boolean => {
  if (tableData.length === 0) return false;

  const pageItems = tableData.slice(startIndex, endIndex + 1);
  if (pageItems.length === 0) return false;

  return pageItems.every(
    (row) =>
      row[SftpPushPendingRecordsTableColumnKeys.SELECT]?.isChecked === true,
  );
};
